import { isPristine } from 'redux-form'
import {
  WelcomeScreen,
  Participation,
  ExamDecisionIndicator,
  RosterManagement,
} from '../../components/setup'
import { CoordinatorSecondaryNavigation } from '../../components/navigation'
import { Loader } from '../../components/common'
import CoordinatorSetupSchoolInfoForm from '../forms/CoordinatorSetupSchoolInfoForm'
import CoordinatorSetupExamAdminForm from '../forms/CoordinatorSetupExamAdminForm'
import { changeStep } from '../../actions/settingsOrg'
import {
  SCHOOL_INFORMATION_FORM,
  EXAM_ADMINISTRATION_FORM,
  EXAM_DECISION_INDICATOR_FORM,
  ROSTER_MANAGEMENT_FORM,
  BIG_FUTURE_AND_CONNECTIONS_FORM,
  AP_AGREEMENT_FORM,
  SETUP_STEP_WELCOME,
  SETUP_STEP_SCHOOL_INFO,
  SETUP_STEP_EXAM_ADMIN,
  SETUP_STEP_EXAM_DECISION,
  SETUP_STEP_ROSTER_MGMT_L1,
  SETUP_STEP_ROSTER_MGMT_L2,
  SETUP_STEP_PARTICIPATION_L1,
  SETUP_STEP_PARTICIPATION_L2,
  SETUP_STEP_BIG_FUTURE_L1,
  SETUP_STEP_BIG_FUTURE_L2,
} from '../../constants/SetupConstants'
import { isFeatureEnabled } from '../../selectors/features'
import ApAgreement from '../../components/setup/apagreement'
import Bigfuture from '../../components/setup/bigfuture'

const findSetupStep = (
  {
    schoolInformation = {},
    examAdministration = {},
    participationForm = {},
    examDecision = {},
    rosterManagement = {},
  },
  step,
  isLevelOne
) => {
  const PART_FORM_OR_AP_AGREEMENT_STEP = isLevelOne
    ? SETUP_STEP_PARTICIPATION_L1
    : SETUP_STEP_PARTICIPATION_L2
  const BIG_FUTURE_STEP = isLevelOne ? SETUP_STEP_BIG_FUTURE_L1 : SETUP_STEP_BIG_FUTURE_L2
  const ROSTER_MANAGEMENT_STEP = isLevelOne ? SETUP_STEP_ROSTER_MGMT_L1 : SETUP_STEP_ROSTER_MGMT_L2

  // If step is explicitly provided, use it
  if (step) {
    return step
  }

  // Stay on Welcome screen if School Information not yet completed
  if (!schoolInformation.complete) {
    return SETUP_STEP_WELCOME
  }

  if (!examAdministration.complete) {
    return SETUP_STEP_EXAM_ADMIN
  }

  if (!isLevelOne && !examDecision.complete) {
    // Level 2 schools have Exam Decision Indicator as step 3
    return SETUP_STEP_EXAM_DECISION
  }

  if (!rosterManagement.complete) {
    return ROSTER_MANAGEMENT_STEP
  }

  if (!participationForm.complete) {
    return PART_FORM_OR_AP_AGREEMENT_STEP
  }

  // All required steps complete, go to Big Future (optional step)
  return BIG_FUTURE_STEP
}

const mapStateToProps = state => {
  const { selectedOrgId, selectedRole, roles } = state.user.data
  const { selectedEducationPeriod } = state.settingsEducationPeriod
  const bigFutureEnabled = isFeatureEnabled(state, 'BIGFUTURE')
  const isMultiOrg = roles.length > 1
  const { setupStep, [selectedOrgId]: settings = {} } = state.settingsOrg
  const isLevelOne =
    roles[selectedRole].level === 1 || settings?.data?.examAdministration?.orderByAnotherSchool
  const isBigFutureApplicableForOrg = settings?.data?.bigFuture !== undefined && bigFutureEnabled

  const setupFormFlow = isLevelOne
    ? {
        [SETUP_STEP_SCHOOL_INFO]: SCHOOL_INFORMATION_FORM,
        [SETUP_STEP_EXAM_ADMIN]: EXAM_ADMINISTRATION_FORM,
        [SETUP_STEP_ROSTER_MGMT_L1]: ROSTER_MANAGEMENT_FORM,
        ...(bigFutureEnabled ? { [SETUP_STEP_PARTICIPATION_L1]: AP_AGREEMENT_FORM } : {}),
        ...(isBigFutureApplicableForOrg
          ? { [SETUP_STEP_BIG_FUTURE_L1]: BIG_FUTURE_AND_CONNECTIONS_FORM }
          : {}),
      }
    : {
        [SETUP_STEP_SCHOOL_INFO]: SCHOOL_INFORMATION_FORM,
        [SETUP_STEP_EXAM_ADMIN]: EXAM_ADMINISTRATION_FORM,
        [SETUP_STEP_EXAM_DECISION]: EXAM_DECISION_INDICATOR_FORM,
        [SETUP_STEP_ROSTER_MGMT_L2]: ROSTER_MANAGEMENT_FORM,
        ...(bigFutureEnabled ? { [SETUP_STEP_PARTICIPATION_L2]: AP_AGREEMENT_FORM } : {}),
        ...(isBigFutureApplicableForOrg
          ? { [SETUP_STEP_BIG_FUTURE_L2]: BIG_FUTURE_AND_CONNECTIONS_FORM }
          : {}),
      }

  return {
    breakpoint: state.app.breakpoint.name,
    hasFormChanged: !isPristine(setupFormFlow[setupStep])(state),
    selectedOrgId,
    isLevelOne,
    setupFormFlow,
    educationPeriodCd: selectedEducationPeriod,
    isMultiOrg,
    setupStep,
    bigFutureEnabled,
    isBigFutureApplicableForOrg,
    ...settings,
  }
}

class CoordinatorSetup extends Component {
  componentDidMount() {
    this.findCorrectStep()
  }

  componentDidUpdate(prevProps) {
    const { data } = this.props
    // if(!fetching
    //    && (prevProps.selectedOrgId !== selectedOrgId
    //      || prevProps.educationPeriodCd !== educationPeriodCd
    //      || !fetched)
    //  ) {
    //  fetchOrgSettings(selectedOrgId, educationPeriodCd)
    // }

    if (prevProps.data !== data) {
      this.findCorrectStep()
    }
  }

  findCorrectStep() {
    const { changeStep, setupStep, data = {}, educationPeriodCd, isLevelOne } = this.props
    if (educationPeriodCd === data.educationPeriodCd) {
      const newStep = findSetupStep(data, setupStep, isLevelOne)

      if (setupStep === null || newStep !== setupStep) {
        changeStep(newStep)
      }
    }
  }

  renderStep() {
    const {
      setupStep,
      fetching,
      data = {},
      bigFutureEnabled,
      isBigFutureApplicableForOrg,
      isMultiOrg,
      isLevelOne,
    } = this.props

    if (fetching && setupStep) {
      return null
    }

    if (fetching && !isMultiOrg) {
      return <Loader />
    }

    // first three steps are common
    if (setupStep === SETUP_STEP_WELCOME) {
      return (
        <WelcomeScreen
          nextStep={findSetupStep(data, setupStep, isLevelOne) || SETUP_STEP_SCHOOL_INFO}
        />
      )
    }
    if (setupStep === SETUP_STEP_SCHOOL_INFO) {
      return <CoordinatorSetupSchoolInfoForm backStep={SETUP_STEP_WELCOME} />
    }
    if (setupStep === SETUP_STEP_EXAM_ADMIN) {
      return <CoordinatorSetupExamAdminForm backStep={SETUP_STEP_SCHOOL_INFO} />
    }

    if (isLevelOne) {
      // level1 setup flow
      if (setupStep === SETUP_STEP_ROSTER_MGMT_L1) {
        return <RosterManagement backStep={SETUP_STEP_EXAM_ADMIN} />
      }
      if (setupStep === SETUP_STEP_PARTICIPATION_L1) {
        return bigFutureEnabled ? (
          <ApAgreement backStep={SETUP_STEP_ROSTER_MGMT_L1} />
        ) : (
          <Participation backStep={SETUP_STEP_ROSTER_MGMT_L1} />
        )
      }
      if (setupStep === SETUP_STEP_BIG_FUTURE_L1) {
        return isBigFutureApplicableForOrg ? (
          <Bigfuture backStep={SETUP_STEP_PARTICIPATION_L1} />
        ) : null
      }
    } else {
      // level2 setup flow
      if (setupStep === SETUP_STEP_EXAM_DECISION) {
        return <ExamDecisionIndicator backStep={SETUP_STEP_EXAM_ADMIN} />
      }
      if (setupStep === SETUP_STEP_ROSTER_MGMT_L2) {
        return <RosterManagement backStep={SETUP_STEP_EXAM_DECISION} />
      }
      if (setupStep === SETUP_STEP_PARTICIPATION_L2) {
        return bigFutureEnabled ? (
          <ApAgreement backStep={SETUP_STEP_ROSTER_MGMT_L2} />
        ) : (
          <Participation backStep={SETUP_STEP_ROSTER_MGMT_L2} />
        )
      }
      if (setupStep === SETUP_STEP_BIG_FUTURE_L2) {
        return isBigFutureApplicableForOrg ? (
          <Bigfuture backStep={SETUP_STEP_PARTICIPATION_L2} />
        ) : null
      }
    }

    return null
  }

  render() {
    const { breakpoint, setupFormFlow, setupStep, hasFormChanged, changeStep } = this.props
    const style =
      breakpoint === 'mobile' || breakpoint === 'tablet'
        ? { marginTop: '20px' }
        : { marginTop: '40px' }

    return (
      <div>
        <CoordinatorSecondaryNavigation
          unsavedForm={hasFormChanged && setupFormFlow[setupStep]}
          onRoleChange={() => changeStep(null)}
        />
        <div className="container" style={style}>
          {this.renderStep()}
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, { changeStep })(CoordinatorSetup)
